import { useEffect, useEffectEvent, useState } from "react";
import { ApiError } from "../../api/client";
import { prepare, resend, type PendingRequest } from "../../api/pending";
import type { CreateOrderRequest, Discount, Order, PaymentMethod, PaymentRequest } from "../../api/types";
import { useTill } from "../../app/till-context";
import { checkoutDiscounts } from "../general/general";
import { t } from "../../i18n";
import { formatAmount, formatEuro, type Cents } from "../../lib/money";
import { touchScreen } from "../../lib/touch";
import { QrCode } from "../../ui/QrCode";
import { Dialog } from "../../ui/Dialog";
import { Keypad } from "../../ui/Keypad";
import { DiscountButtons } from "./DiscountButtons";
import {
  changeFor,
  needsConfirmation,
  addNote,
  changeForTyped,
  planPayment,
  remainingAfter,
  type PlanResult,
} from "./payment-plan";

/** A new sale (order and payments saved together) or paying the rest of an open order. */
export type CheckoutTarget =
  | {
      kind: "new";
      request: CreateOrderRequest;
      totalCents: Cents | null;
      discount: Discount | null;
      /** The code of whoever allowed something free or a discount on this order. */
      staffToken: string | undefined;
    }
  | { kind: "existing"; order: Order };

/** Spaarkaart of a known customer: the card and whether it is used on this order. */
export interface Loyalty {
  points: number;
  goal: number;
  rewardCents: Cents;
  used: boolean;
}

interface Props {
  target: CheckoutTarget;
  onDiscount?: (discount: Discount | null) => void;
  /** A known customer with the action Spaarkaart on: the full card can be used (prototype). */
  loyalty?: Loyalty | undefined;
  onRedeem?: (used: boolean) => void;
  onPaid: (order: Order) => void;
  /**
   * Closed while the server has not confirmed the sale yet. The till keeps sending it by itself
   * (see TillShell), so the ticket must be cleared: selling it again would sell it twice.
   */
  onDeferred?: () => void;
  onClose: () => void;
}

const NOTES: Cents[] = [500, 1_000, 2_000, 5_000, 10_000];
type Phase =
  | { name: "collect" }
  | { name: "confirm"; payment: PaymentRequest }
  | { name: "sending"; request: PendingRequest }
  | { name: "failed"; request: PendingRequest; message: string }
  | { name: "done"; order: Order; changeCents: Cents }
  /** The Bancontact Pro QR also on the screen of a phone, until Bancontact says it is paid. */
  | { name: "qr"; order: Order };

/**
 * Checkout as in the prototype: amount keypad on the right, payment methods below it, discounts on
 * the left. Partial amounts collect split payments; everything is sent in one request once the
 * total is covered, with one idempotency key (ADR 0006), so a retry can never charge twice.
 */
export function CheckoutDialog({ target, onDiscount, loyalty, onRedeem, onPaid, onDeferred, onClose }: Props) {
  const { api, paymentMethods, receiptQr, toast, general } = useTill();
  // the discounts the shop chose in Beheer › Algemeen (Furkan, 13/10/2026)
  const discounts = checkoutDiscounts(general);
  const [typed, setTyped] = useState("");
  const [collected, setCollected] = useState<PaymentRequest[]>([]);
  const [phase, setPhase] = useState<Phase>({ name: "collect" });
  const [error, setError] = useState<string | null>(null);

  const due = target.kind === "new" ? target.totalCents : target.order.totalCents - target.order.paidCents;
  const remaining = due === null ? null : remainingAfter(due, collected);
  const methods = paymentMethods;
  const busy = phase.name === "sending";

  const send = async (request: PendingRequest, payments: PaymentRequest[]) => {
    setPhase({ name: "sending", request });
    try {
      const order = await resend<Order>(api, request);
      const changeCents = changeFor(payments);
      // no extra confirmation: only when money has to be handed back is it shown, as a help
      if (changeCents > 0) setPhase({ name: "done", order, changeCents });
      else onPaid(order);
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryable) {
        setPhase({ name: "failed", request, message: failure.message });
      } else {
        // refused by the server: nothing was recorded; the cashier can correct and try again
        setCollected([]);
        setPhase({ name: "collect" });
        setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      }
    }
  };

  const record = (payment: PaymentRequest) => {
    const payments = [...collected, payment];
    setTyped("");
    setError(null);
    if (due === null || remainingAfter(due, payments) > 0) {
      setCollected(payments);
      setPhase({ name: "collect" });
      return;
    }
    const request =
      target.kind === "new"
        ? prepare("/orders", { ...target.request, payments }, `${t.checkout.title} ${formatEuro(due)}`, {
            staffToken: target.staffToken,
          })
        : prepare(`/orders/${target.order.id}/payments`, { payments }, `${t.checkout.title} ${target.order.code}`);
    setCollected(payments);
    void send(request, payments);
  };

  const apply = (result: PlanResult) => {
    if (!result.ok) {
      setError(result.reason === "tooMuch" ? t.checkout.tooMuch : t.checkout.invalidAmount);
      return;
    }
    if (needsConfirmation(result.payment.method)) setPhase({ name: "confirm", payment: result.payment });
    else record(result.payment);
  };

  /**
   * "Bancontact Pro QR" in a shop that prints it on the receipt (ADR 0029): nothing is taken now. The
   * order is saved unpaid and its receipt carries a QR for what is due; Tafo books the payment itself
   * when Bancontact says the customer paid, at the counter or at the door.
   */
  const printQr = async () => {
    if (due === null || remaining === null) return;
    if (collected.length > 0 || typed !== "") {
      setError(t.checkout.receiptQr.whole);
      return;
    }
    const request =
      target.kind === "new"
        ? prepare("/orders", { ...target.request, receiptQr: true }, `${t.checkout.title} ${formatEuro(due)}`, {
            staffToken: target.staffToken,
          })
        : prepare(`/orders/${target.order.id}/receipt-qr`, {}, `${t.checkout.title} ${target.order.code}`);
    setPhase({ name: "sending", request });
    setError(null);
    try {
      const order = await resend<Order>(api, request);
      toast(t.checkout.receiptQr.printed(formatEuro(remaining)));
      // on a phone the customer can scan the screen too, while the receipt prints (Furkan, 18/10/2026)
      if (touchScreen() && order.receiptQr !== null) setPhase({ name: "qr", order });
      else onPaid(order);
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryable) {
        setPhase({ name: "failed", request, message: failure.message });
      } else {
        setPhase({ name: "collect" });
        setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      }
    }
  };

  const choose = (method: PaymentMethod) => {
    if (remaining === null || phase.name !== "collect") return;
    if (method === "bank_qr" && receiptQr && remaining > 0) {
      void printQr();
      return;
    }
    // everything is free: nothing to pay, the order is saved as settled
    if (remaining === 0 && target.kind === "new" && collected.length === 0) {
      void send(prepare("/orders", target.request, t.checkout.title, { staffToken: target.staffToken }), []);
      return;
    }
    apply(planPayment(method, typed, remaining));
  };

  const onKey = useEffectEvent((event: KeyboardEvent) => {
    if (phase.name === "done" && event.key === "Enter") {
      event.preventDefault();
      onPaid(phase.order);
    } else if (event.key === "F12" && methods.includes("cash")) {
      event.preventDefault();
      choose("cash");
    } else if (event.key === "F11" && methods.includes("bancontact")) {
      event.preventDefault();
      choose("bancontact");
    }
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      onKey(event);
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  if (phase.name === "qr") {
    return <ScreenQr order={phase.order} onDone={onPaid} />;
  }

  if (phase.name === "done") {
    const { order, changeCents } = phase;
    return (
      <Dialog title={t.checkout.done} size="narrow" onClose={() => onPaid(order)}>
        <div className="done">
          <p className="muted">
            {t.orders.code} <span className="code">{order.code}</span> · {formatEuro(order.totalCents)}
          </p>
          {changeCents > 0 ? (
            <p className="big change num">{t.checkout.giveChange(formatEuro(changeCents))}</p>
          ) : (
            <p className="big num">{formatEuro(order.paidCents)}</p>
          )}
          <button type="button" className="btn primary" data-autofocus onClick={() => onPaid(order)}>
            {t.checkout.next} <kbd>Enter</kbd>
          </button>
        </div>
      </Dialog>
    );
  }

  const close = () => {
    if (phase.name === "failed") onDeferred?.();
    onClose();
  };

  // a discount on a new order, and on one that is already there (a table that is paid) as long as nothing
  // was paid of it (Furkan, 13/10/2026: "Tafel afrekenen: geen −4 € korting")
  const canDiscount =
    onDiscount !== undefined &&
    discounts.length > 0 &&
    collected.length === 0 &&
    (target.kind === "new" || target.order.paidCents === 0);
  const chosenDiscount = target.kind === "new" ? target.discount : target.order.discount;

  return (
    <Dialog title={t.checkout.title} onClose={busy ? () => undefined : close} size="checkout">
      <div className="co">
        <div className="co-left">
          <div className="sum">
            <div className="t">
              <span>{t.checkout.total}</span>
              <span className="num">
                {target.kind === "new"
                  ? target.totalCents === null
                    ? "…"
                    : formatEuro(target.totalCents)
                  : formatEuro(target.order.totalCents)}
              </span>
            </div>
            {collected.length > 0 && (
              <div className="paid-list">
                {collected.map((payment, index) => (
                  <div key={index}>
                    <span>{t.paymentMethods[payment.method]}</span>
                    <span className="num">{formatEuro(payment.amountCents)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="t">
              <span>{t.checkout.due}</span>
              <span className="due num" data-testid="due">
                {remaining === null ? "…" : formatEuro(remaining)}
              </span>
            </div>
            {remaining !== null && changeForTyped(typed, remaining) > 0 && (
              <div className="t change">
                <span>{t.checkout.giveBackLive}</span>
                <span className="num" data-testid="change">
                  {formatEuro(changeForTyped(typed, remaining))}
                </span>
              </div>
            )}
          </div>

          {canDiscount && (
            <div className="grp">
              <h3>{t.checkout.discount}</h3>
              <DiscountButtons discounts={discounts} chosen={chosenDiscount} onChoose={onDiscount} />
            </div>
          )}

          {loyalty && onRedeem && target.kind === "new" && (
            <button
              type="button"
              className={`btn loyalty${loyalty.used ? " primary" : ""}`}
              aria-pressed={loyalty.used}
              disabled={(loyalty.points < loyalty.goal && !loyalty.used) || collected.length > 0}
              onClick={() => {
                onRedeem(!loyalty.used);
              }}
            >
              {t.checkout.loyalty(loyalty.points, loyalty.goal, formatEuro(loyalty.rewardCents))}
            </button>
          )}

          {phase.name === "failed" && (
            <div className="banner bad" role="alert">
              <span>{t.checkout.unconfirmed}</span>
              <button type="button" className="btn small" onClick={() => void send(phase.request, collected)}>
                {t.checkout.retry}
              </button>
            </div>
          )}
          {phase.name === "sending" && <p className="muted">{t.checkout.sending}</p>}
          {error !== null && (
            <p className="error-text" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="co-right">
          {phase.name === "confirm" ? (
            <div className="confirm">
              <p className="confirm-question">
                {t.checkout.confirmTerminal(
                  t.paymentMethods[phase.payment.method],
                  formatEuro(phase.payment.amountCents),
                )}
              </p>
              {phase.payment.method === "bank_qr" && !receiptQr && (
                <p className="muted small">{t.checkout.receiptQr.off}</p>
              )}
              <button type="button" className="btn primary" data-autofocus onClick={() => record(phase.payment)}>
                {t.checkout.confirmYes}
              </button>
              <button type="button" className="btn" onClick={() => setPhase({ name: "collect" })}>
                {t.checkout.confirmNo}
              </button>
            </div>
          ) : (
            <>
              <input
                className="amountfield num"
                value={typed}
                placeholder={remaining === null ? "" : formatAmount(remaining)}
                aria-label="Bedrag"
                inputMode="decimal"
                disabled={busy || phase.name === "failed"}
                onChange={(event) => {
                  setTyped(event.target.value.replace(/[^\d.,]/g, ""));
                  setError(null);
                }}
                onKeyDown={(event) => {
                  // Enter in the amount = Contant (prototype)
                  if (event.key === "Enter" && methods.includes("cash")) {
                    event.preventDefault();
                    choose("cash");
                  }
                }}
                data-autofocus
              />
              {methods.includes("cash") && (
                <div className="cash">
                  {NOTES.map((note) => (
                    <button
                      key={note}
                      type="button"
                      disabled={busy || remaining === null || phase.name === "failed"}
                      onClick={() => {
                        setTyped((current) => addNote(current, note));
                        setError(null);
                      }}
                    >
                      €{note / 100}
                    </button>
                  ))}
                </div>
              )}
              <Keypad
                disabled={busy || phase.name === "failed"}
                onDigit={(digit) => setTyped((current) => current + digit)}
                left={{
                  label: ",",
                  onPress: () => setTyped((current) => (current.includes(",") ? current : `${current},`)),
                }}
                right={{
                  label: "⌫",
                  ariaLabel: "Wis laatste cijfer",
                  onPress: () => setTyped((current) => current.slice(0, -1)),
                }}
              />
              <div className="pms">
                {methods.map((method) => (
                  <button
                    key={method}
                    type="button"
                    className={`pm ${method === "cash" ? "cash-method" : ""}`}
                    disabled={busy || remaining === null || phase.name === "failed"}
                    onClick={() => choose(method)}
                  >
                    {/* with the product of Bancontact set, this button prints the QR on the receipt */}
                    {method === "bank_qr" && receiptQr ? t.checkout.receiptQr.button : t.paymentMethods[method]}
                    {method === "cash" && <kbd>F12</kbd>}
                    {method === "bancontact" && <kbd>F11</kbd>}
                  </button>
                ))}
              </div>
              <p className="muted small">{t.checkout.amountHint}</p>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/** How often the screen asks whether Bancontact confirmed the QR. */
const QR_ASK_MS = 2_000;

/**
 * The Bancontact Pro QR of the receipt, big on the screen of a phone: the customer scans the phone or the
 * receipt, whichever is nearer. Tafo books the payment when Bancontact confirms it (ADR 0029); this
 * screen only asks how far it is and says so. Closing it does not stop anything: the QR on the receipt
 * stays valid.
 */
export function ScreenQr({ order, onDone }: { order: Order; onDone: (order: Order) => void }) {
  const { api } = useTill();
  const text = t.checkout.receiptQr;
  const [latest, setLatest] = useState(order);
  const paid = latest.status === "paid" || latest.paidCents >= latest.totalCents;

  useEffect(() => {
    if (paid) return;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      api
        .request<Order>("GET", `/orders/${order.id}`, { signal: controller.signal })
        .then(setLatest)
        // out of reach for a moment: the next round asks again
        .catch(() => undefined);
    }, QR_ASK_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [api, order.id, paid]);

  const qr = order.receiptQr!;
  return (
    <Dialog title={text.onReceipt} size="narrow" onClose={() => onDone(latest)}>
      <div className="screen-qr">
        {paid ? (
          <p className="big num screen-qr-paid">{text.screenPaid(formatEuro(qr.amountCents))}</p>
        ) : (
          <>
            <div className="screen-qr-code">
              <QrCode text={qr.text} label={text.onReceipt} />
            </div>
            <p className="big num">{formatEuro(qr.amountCents)}</p>
            <p className="muted">{text.screenWaiting}</p>
          </>
        )}
        <button type="button" className="btn primary" onClick={() => onDone(latest)}>
          {paid ? t.checkout.next : text.screenClose}
        </button>
      </div>
    </Dialog>
  );
}
